/**
 * SlipToasts — loose paper slips drifting onto the painting's lower corner.
 *
 * The one C5 element allowed to trespass on the plate (doctrine §"Hard rules"
 * 2). Ordering rules live in the pure `slipQueue` module; this class owns the
 * DOM elements and timers only.
 *
 * Motion is gentle: a short drift + fade in under 250ms, a long dwell, then a
 * fade out (doctrine rule 4).
 */

import { getString } from '../locale';
import {
  MAX_VISIBLE_SLIPS,
  SLIP_HOLD_MS,
  pushSlip,
  removeSlip,
  type SlipEntry,
} from './slipQueue';

const FADE_OUT_MS = 240;

export interface SlipOptions {
  /** Dwell before the slip retires, in ms. */
  holdMs?: number;
}

export class SlipToasts {
  private readonly container: HTMLDivElement;
  private queue: SlipEntry[] = [];
  private readonly nodes = new Map<number, HTMLDivElement>();
  private readonly timers = new Map<number, number>();
  private nextId = 1;

  constructor(parent: HTMLElement) {
    this.container = document.createElement('div');
    this.container.className = 'c5-slips';
    this.container.setAttribute('role', 'status');
    this.container.setAttribute('aria-live', 'polite');
    this.container.setAttribute('aria-label', getString('c5.a11y.slips'));
    parent.appendChild(this.container);
  }

  /** Show a slip. Returns its id so callers can retire it early. */
  showSlip(text: string, opts: SlipOptions = {}): number {
    const trimmed = text.trim();
    if (trimmed.length === 0) return -1;

    const entry: SlipEntry = { id: this.nextId++, text: trimmed, at: Date.now() };
    const { queue, retired } = pushSlip(this.queue, entry, MAX_VISIBLE_SLIPS);
    this.queue = queue;
    for (const old of retired) this.retire(old.id);

    const node = document.createElement('div');
    node.className = 'c5-slip';
    node.textContent = trimmed;
    this.container.appendChild(node);
    this.nodes.set(entry.id, node);

    // Next frame so the transition has a start value to animate from.
    requestAnimationFrame(() => node.classList.add('c5-slip-in'));

    const hold = opts.holdMs ?? SLIP_HOLD_MS;
    this.timers.set(
      entry.id,
      window.setTimeout(() => this.retire(entry.id), hold),
    );
    return entry.id;
  }

  /** Fade a slip out and drop it from the queue. */
  retire(id: number): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      this.timers.delete(id);
    }
    this.queue = removeSlip(this.queue, id);

    const node = this.nodes.get(id);
    if (!node) return;
    this.nodes.delete(id);
    node.classList.remove('c5-slip-in');
    node.classList.add('c5-slip-out');
    window.setTimeout(() => node.remove(), FADE_OUT_MS);
  }

  /** Retire everything immediately (zone change, teardown). */
  clear(): void {
    for (const id of [...this.nodes.keys()]) this.retire(id);
  }

  destroy(): void {
    for (const timer of this.timers.values()) window.clearTimeout(timer);
    this.timers.clear();
    this.nodes.clear();
    this.queue = [];
    this.container.remove();
  }
}
